import { ref, reactive, computed, onMounted } from 'vue'
import { config } from '@/config'
import type { ICheckoutConfig } from '@/config'
import type { ComputedRef } from 'vue'

type TCountryInfo = {
  isUK: boolean
  isEU: boolean
  isUSA: boolean
  countryCode: string
  countryName: string
  expiry?: number
}

const CACHE_KEY = 'tenorlab_geo_info'
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

export const useCountryInfo = (): {
  countryInfo: ComputedRef<TCountryInfo>
  defaultCheckoutConfig: ComputedRef<ICheckoutConfig>
  otherCheckoutConfig: ComputedRef<ICheckoutConfig>
} => {
  const _countryInfo = reactive<TCountryInfo>({
    isUK: false,
    isEU: false,
    isUSA: true, // Defaulting to US/Global (Polar.sh)
    countryCode: 'US',
    countryName: 'United States',
  })

  const _checkoutConfig = ref<ICheckoutConfig>(config.defaultCheckoutConfig)
  const _otherCheckoutConfig = ref<ICheckoutConfig>(config.europeOrUKCheckoutConfig)

  const updateState = (data: any) => {
    // prettier-ignore
    const euCodes = [
      // EU Member States
      'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 
      'FR', 'GR', 'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 
      'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK',
      // Non-EU European Territories (VAT aligned / Continental)
      'MC', 'IM', 'AD', 'GI', 'LI', 'NO', 'IS', 'SM', 'VA', 'FO'
    ]

    const code = data.country_code || 'US'
    _countryInfo.countryCode = code
    _countryInfo.countryName = data.country_name || 'United States'
    _countryInfo.isUK = code === 'GB'
    _countryInfo.isEU = euCodes.includes(code)
    _countryInfo.isUSA = code === 'US'

    if (_countryInfo.isUK || _countryInfo.isEU) {
      _checkoutConfig.value = config.europeOrUKCheckoutConfig
      _otherCheckoutConfig.value = config.defaultCheckoutConfig
    }
  }

  onMounted(async () => {
    // 1. Check Local Cache
    const cached = localStorage.getItem(CACHE_KEY)
    if (cached) {
      try {
        const parsed = JSON.parse(cached)
        if (Date.now() < parsed.expiry) {
          updateState(parsed)
          return
        }
      } catch (e) {
        localStorage.removeItem(CACHE_KEY)
      }
    }

    // 2. Fetch if no valid cache
    try {
      const res = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(3000) })
      const data = await res.json()

      // 3. Persist to Cache
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          ...data,
          expiry: Date.now() + CACHE_DURATION,
        }),
      )

      updateState(data)
    } catch (e) {
      console.warn('Geo-detection failed, falling back to US default.')
    }
  })

  return {
    countryInfo: computed(() => _countryInfo),
    defaultCheckoutConfig: computed(() => _checkoutConfig.value),
    otherCheckoutConfig: computed(() => _otherCheckoutConfig.value),
  }
}
